import React from 'react';
import { Card } from 'antd';
import { useLocation } from 'react-router-dom';
import { SkBar } from './_primitives';

const GamificationHeader = () => (
  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4 sm:mb-6">
    <div className="flex flex-col gap-2 min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <SkBar h="h-3" w="w-16" />
        <SkBar h="h-3" w="w-2" />
        <SkBar h="h-3" w="w-40" />
      </div>
      <SkBar h="h-7" w="w-72" className="max-w-full mt-1" />
      <SkBar h="h-3" w="w-96" className="max-w-full" />
    </div>
    <SkBar h="h-9" w="w-28" className="rounded-md flex-shrink-0" />
  </div>
);

const TabsRow = ({ active = 0 }) => (
  <div className="flex gap-6 border-b border-gray-200 mb-6 overflow-x-auto">
    {Array.from({ length: 2 }).map((_, i) => (
      <div
        key={i}
        className={`pb-2 flex items-center gap-2 flex-shrink-0 ${
          active === i ? 'border-b-2 border-gray-300' : ''
        }`}
      >
        <div className="w-4 h-4 rounded bg-gray-200 animate-pulse" />
        <SkBar h="h-4" w="w-24" />
      </div>
    ))}
  </div>
);

const InfoBanner = ({ tone = 'blue' }) => {
  const palette =
    tone === 'amber'
      ? 'border-amber-200 bg-amber-50'
      : 'border-blue-200 bg-blue-50';
  const dot = tone === 'amber' ? 'bg-amber-200' : 'bg-blue-200';
  return (
    <div className={`p-4 border ${palette} rounded-lg flex items-start gap-3 mb-4`}>
      <div className={`w-6 h-6 rounded-full ${dot} animate-pulse flex-shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <SkBar h="h-4" w="w-48" />
        <SkBar h="h-3" w="w-full" />
        <SkBar h="h-3" w="w-3/4" />
      </div>
    </div>
  );
};

/* ─────────── Spin Wheel tab ─────────── */

const SpinStatCard = () => (
  <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
    <div className="flex flex-col items-center gap-3">
      <SkBar h="h-3" w="w-24" />
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded bg-gray-200 animate-pulse" />
        <SkBar h="h-6" w="w-20" />
      </div>
      <SkBar h="h-2" w="w-full" className="rounded-full" />
    </div>
  </Card>
);

const SpinConfigHeader = () => (
  <div className="flex items-center justify-between gap-3 mb-4 pb-4 border-b border-gray-100">
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-5 h-5 rounded bg-gray-200 animate-pulse flex-shrink-0" />
      <SkBar h="h-5" w="w-48" />
      <SkBar h="h-5" w="w-14" className="rounded-md" />
    </div>
    <SkBar h="h-9" w="w-40" className="rounded-md flex-shrink-0" />
  </div>
);

const SpinFormRow = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
    {/* Enable Spin Wheel — toggle */}
    <div className="flex flex-col gap-2">
      <SkBar h="h-3" w="w-32" />
      <SkBar h="h-6" w="w-12" className="rounded-full" />
      <SkBar h="h-2" w="w-40" />
    </div>
    {/* Spin Cost */}
    <div className="flex flex-col gap-2">
      <SkBar h="h-3" w="w-32" />
      <SkBar h="h-10" w="w-full" className="rounded-md" />
      <SkBar h="h-2" w="w-32" />
    </div>
    {/* Max Spins Per Day */}
    <div className="flex flex-col gap-2">
      <SkBar h="h-3" w="w-32" />
      <SkBar h="h-10" w="w-full" className="rounded-md" />
      <SkBar h="h-2" w="w-40" />
    </div>
  </div>
);

const SectionDivider = () => (
  <div className="flex items-center gap-2 pb-2 border-b border-gray-200 mb-4">
    <div className="w-5 h-5 rounded bg-gray-200 animate-pulse" />
    <SkBar h="h-4" w="w-32" />
  </div>
);

const SegmentCard = () => (
  <div className="relative p-4 border border-gray-200 rounded-lg border-l-4 bg-white">
    {/* % badge top-right */}
    <div className="absolute top-2 right-2">
      <SkBar h="h-5" w="w-12" className="rounded-md" />
    </div>
    <div className="flex items-start gap-3">
      {/* Points Value */}
      <div className="flex-1 flex flex-col gap-2 min-w-0">
        <SkBar h="h-3" w="w-24" />
        <SkBar h="h-10" w="w-full" className="rounded-md" />
      </div>
      {/* Weight */}
      <div className="flex-1 flex flex-col gap-2 min-w-0">
        <SkBar h="h-3" w="w-28" />
        <div className="flex items-center gap-2">
          <SkBar h="h-10" w="w-full" className="rounded-md" />
          <SkBar h="h-8" w="w-8" className="rounded-md flex-shrink-0" />
        </div>
      </div>
    </div>
  </div>
);

const AddSegmentRow = () => (
  <div className="mt-2 p-3 border border-dashed border-gray-300 rounded-lg flex items-center justify-center gap-2">
    <div className="w-4 h-4 rounded bg-gray-200 animate-pulse" />
    <SkBar h="h-3" w="w-28" />
  </div>
);

const MetadataFooter = () => (
  <div className="mt-4 p-3 bg-indigo-50 border border-indigo-100 rounded-lg grid grid-cols-1 sm:grid-cols-3 gap-3">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="flex flex-col gap-1.5">
        <SkBar h="h-3" w="w-28" />
        <SkBar h="h-3" w="w-40" />
      </div>
    ))}
  </div>
);

const SpinWheelTabContent = () => (
  <>
    <InfoBanner tone="blue" />

    {/* 3 centered stat cards */}
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <SpinStatCard key={i} />
      ))}
    </div>

    {/* Spin Wheel Configuration card */}
    <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
      <SpinConfigHeader />
      <SpinFormRow />

      <SectionDivider />
      <InfoBanner tone="amber" />

      {/* 2-col grid of segment cards (8 segments) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <SegmentCard key={i} />
        ))}
      </div>

      <AddSegmentRow />
      <MetadataFooter />
    </Card>
  </>
);

/* ─────────── Daily Bonus tab ─────────── */

const ChallengeField = () => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 rounded bg-gray-200 animate-pulse" />
      <SkBar h="h-3" w="w-28" />
    </div>
    <div className="flex items-center gap-2">
      <SkBar h="h-10" w="w-full" className="rounded-md" />
      <SkBar h="h-8" w="w-10" className="rounded-md flex-shrink-0" />
    </div>
    <SkBar h="h-2" w="w-40" className="max-w-full" />
  </div>
);

const SectionLabel = () => (
  <div className="flex items-center gap-3 my-6">
    <div className="flex-1 h-px bg-gray-200" />
    <SkBar h="h-3" w="w-28" />
    <div className="flex-1 h-px bg-gray-200" />
  </div>
);

const EnableToggleField = () => (
  <div className="flex flex-col gap-2 mb-2">
    <SkBar h="h-4" w="w-36" />
    <SkBar h="h-6" w="w-12" className="rounded-full" />
    <SkBar h="h-2" w="w-48" />
  </div>
);

const DailyBonusMetadataFooter = () => (
  <div className="mt-4 p-3 bg-indigo-50 border border-indigo-100 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-3">
    {Array.from({ length: 2 }).map((_, i) => (
      <div key={i} className="flex flex-col gap-1.5">
        <SkBar h="h-3" w="w-28" />
        <SkBar h="h-3" w="w-40" />
      </div>
    ))}
  </div>
);

const DailyBonusTabContent = () => (
  <>
    <InfoBanner tone="blue" />

    <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
      <SpinConfigHeader />

      <EnableToggleField />

      {/* Challenge Points section */}
      <SectionLabel />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <ChallengeField key={i} />
        ))}
      </div>

      {/* Streak Bonus section */}
      <SectionLabel />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <ChallengeField />
      </div>

      <DailyBonusMetadataFooter />
    </Card>
  </>
);

/* ─────────── Main loader ─────────── */

const GamificationConfigPageLoader = () => {
  const { search } = useLocation();
  const tab = new URLSearchParams(search).get('tab');
  const isDailyBonus = tab === 'daily-bonus';

  return (
    <div className="w-full p-3 sm:p-4 md:p-6">
      <GamificationHeader />
      <TabsRow active={isDailyBonus ? 1 : 0} />
      {isDailyBonus ? <DailyBonusTabContent /> : <SpinWheelTabContent />}
    </div>
  );
};

export default GamificationConfigPageLoader;
